import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import workshop3Service from '../services/workshop3Service';
import dashboardService from '../services/dashboardService';

import { markTabAsCompleted } from '../utils/navigationUtils';

const useAtelier4Store = create(
  subscribeWithSelector((set, get) => ({
    attackPaths: [],
    operationalScenarios: [],
    dashboardData: null,
    ctiItemStatuses: {},
    isLoading: false,
    isDataLoaded: false,
    error: null,
    analysisId: null,

    loadData: async (analysisId) => {
      if (!analysisId) return;
      set({ isLoading: true, error: null });
      set({ analysisId });

      try {
        const promises = [
          workshop3Service.getAttackPaths(analysisId),
          workshop3Service.getOperationalScenarios(analysisId),
        ];

        const results = await Promise.allSettled(promises);
        const [attackPathsRes, scenariosRes] = results;
        const errors = {};

        if (attackPathsRes.status === 'fulfilled' && attackPathsRes.value) {
          const pathsData = attackPathsRes.value.success && attackPathsRes.value.data
            ? attackPathsRes.value.data
            : [];
          if (analysisId !== get().analysisId) return;
          set({ attackPaths: pathsData });
          if (pathsData && pathsData.some(path => path.ctiResults && path.ctiResults.assets && path.ctiResults.assets.length > 0)) {
            try { markTabAsCompleted('atelier4-threat-intelligence', analysisId); } catch (e) { }
          }
        } else {
          if (analysisId !== get().analysisId) return;
          set({ attackPaths: [] });
          errors.attackPaths = attackPathsRes.reason?.message || 'Failed to load attack paths';
        }

        if (scenariosRes.status === 'fulfilled' && scenariosRes.value) {
          const responseData = scenariosRes.value;
          const scenariosData = responseData?.data?.scenarios || responseData?.scenarios || [];
          if (analysisId !== get().analysisId) return;
          set({ operationalScenarios: scenariosData });
          if (scenariosData && scenariosData.length > 0) {
            try { markTabAsCompleted('atelier4-cti-scenarios', analysisId); } catch (e) { }
          }
        } else {
          if (analysisId !== get().analysisId) return;
          set({ operationalScenarios: [] });
          errors.operationalScenarios = scenariosRes.reason?.message || 'Failed to load operational scenarios';
        }

        if (analysisId !== get().analysisId) return;
        set({
          isLoading: false,
          isDataLoaded: true,
          error: Object.keys(errors).length > 0 ? errors : null,
        });
      } catch (error) {
        console.error('[useAtelier4Store] Error loading data:', error);
        set({ isLoading: false, error: { general: error.message } });
      }
    },

    loadDashboardData: async (analysisId) => {
      if (!analysisId) return;
      set({ analysisId });
      try {
        const response = await dashboardService.getWorkshop4DashboardData(analysisId);
        if (response.success) {
          if (analysisId !== get().analysisId) return;
          const formattedData = dashboardService.formatWorkshop4Data(response.data);
          set({ dashboardData: formattedData });
        } else {
          if (analysisId !== get().analysisId) return;
          set({ dashboardData: null });
        }
      } catch (error) {
        console.error('[useAtelier4Store] Error loading workshop 4 dashboard data:', error);
        if (analysisId !== get().analysisId) return;
        set({ dashboardData: null });
      }
    },

    updateCtiItemStatus: (itemId, newStatus) => {
      set((state) => ({
        ctiItemStatuses: {
          ...state.ctiItemStatuses,
          [itemId]: newStatus,
        },
      }));
    },

    setOperationalScenarios: (scenarios) => {
      set({ operationalScenarios: scenarios });
    },

    resetState: () => {
      set({
        attackPaths: [],
        operationalScenarios: [],
        dashboardData: null,
        ctiItemStatuses: {},
        isLoading: false,
        isDataLoaded: false,
        error: null,
        analysisId: null,
      });
    },
  }))
);

export default useAtelier4Store;